/*
 * La pestaña Descubrimiento de una cuenta: las cuatro sesiones a la izquierda, el
 * detalle de la sesión elegida en medio, y a la derecha los tres embudos (zonas →
 * cartera → primer encargo), el cierre D5 y la transición siguiente con lo que la
 * bloquea. Las compuertas son advertencias del método con nivel: lo `absoluto`
 * no se salta; lo `duro` se fuerza con motivo y deja huella.
 *
 * Marcar zonas, armar oportunidades, correr la Matriz, validar las compuertas y
 * capturar el cierre se hacen aquí (`Embudos`). Es lo que mueve la fase: sin eso,
 * por muchos datos que se capturen, el descubrimiento no avanza.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useCandidatos, usePersonas, useSesiones, useTransiciones, useVerificados, useVista } from "../../api/hooks";
import { SESIONES, type CasoResumen, type CuentaDetalle, type Sesion, type TipoSesion } from "../../api/tipos";
import { useFormularios } from "../../shell/Formularios";
import { Abierto, CabeceraSeccion, Esqueleto, Num, Pastilla, fmtFecha } from "../../ui/primitivas";
import { BotonArtefacto, BotonGobernanza, Embudos, type Ctx } from "./Embudos";

const ESTADO_SESION = { planificada: ["Agendada", "var(--sky)"], en_curso: ["En curso", "var(--sky)"], cerrada: ["Hecha", "var(--verified)"] } as const;

export function DescubrimientoCuenta({ c, caso }: { c: CuentaDetalle; caso: CasoResumen | null }) {
  const { abrir } = useFormularios();
  const id = caso?.id ?? null;
  const { data: vista, isLoading } = useVista(id);
  const { data: sesiones } = useSesiones(id);
  const { data: personas } = usePersonas(id);
  const { data: candidatos } = useCandidatos(id);
  const { data: verificados } = useVerificados(id);
  const { data: transiciones } = useTransiciones(id);

  const porTipo = useMemo(() => {
    const m = new Map<TipoSesion, Sesion[]>();
    for (const s of sesiones ?? []) m.set(s.tipo, [...(m.get(s.tipo) ?? []), s]);
    return m;
  }, [sesiones]);
  const primeraPendiente = SESIONES.find((s) => !porTipo.has(s.id))?.id ?? "S4_taller";
  const [sel, setSel] = useState<TipoSesion>(primeraPendiente);

  if (!caso) {
    return (
      <Abierto style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ flex: 1, minWidth: 240 }}>El descubrimiento no ha arrancado. Al iniciarlo, la cuenta pasa a <strong>En descubrimiento</strong> y se abre el expediente en D0 · Radar.</span>
        <button className="btn btn-primario" onClick={() => abrir({ tipo: "descubrimiento", cuentaId: c.id, empresa: c.empresa })}>Iniciar descubrimiento →</button>
      </Abierto>
    );
  }
  if (isLoading || !vista) return <div className="tarjeta"><Esqueleto lineas={6} /></div>;

  const ctx: Ctx = { casoId: caso.id, cuentaId: c.id, vista, verificados: verificados ?? [], personas: personas ?? [], sesiones: sesiones ?? [] };
  const def = SESIONES.find((s) => s.id === sel)!;
  const mias = porTipo.get(sel) ?? [];
  const cliente = (personas ?? []).filter((p) => p.organizacion === "cliente");
  const nombre = new Map((personas ?? []).map((p) => [p.id, p.nombre]));

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px,1fr))", gap: 16, alignItems: "start" }}>
      {/* ── Las cuatro sesiones ── */}
      <nav style={{ display: "flex", flexDirection: "column", gap: 2 }} aria-label="Sesiones">
        <span className="tag11" style={{ color: "var(--faint)", display: "block", padding: "0 10px 8px" }}>Las cuatro sesiones</span>
        {SESIONES.map((s) => {
          const ss = porTipo.get(s.id) ?? [];
          const ultima = ss[ss.length - 1];
          const activo = sel === s.id;
          const estado = ultima ? ESTADO_SESION[ultima.estado] : null;
          return (
            <button key={s.id} onClick={() => setSel(s.id)} style={{ display: "flex", gap: 10, alignItems: "flex-start", width: "100%", padding: "9px 10px", borderRadius: "var(--r-control)", background: activo ? "var(--card)" : "transparent", border: activo ? "1px solid var(--border)" : "1px solid transparent" }}>
              <Num n={s.num} hecho={ultima?.estado === "cerrada"} activo={ultima?.estado === "planificada" || ultima?.estado === "en_curso"} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 13.5, fontWeight: activo ? 500 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.nombre}</span>
                <span style={{ display: "block", fontSize: 11.5, color: estado ? estado[1] : "var(--faint)" }}>{ultima ? `${estado![0]} · ${fmtFecha(ultima.fecha, true)}` : "Pendiente"}</span>
              </span>
            </button>
          );
        })}
        <div style={{ margin: "14px 10px 0", paddingTop: 12, borderTop: "1px solid var(--border)", fontSize: 12, color: "var(--faint)", lineHeight: 1.5 }}>
          ~2 semanas · todas grabadas con Plaud, con consentimiento verbal.
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="mono">{vista.caso.estado} · ruta {vista.caso.ruta}</span>
            <span>{vista.caso.facilitador} facilita{vista.caso.comercial ? ` · ${vista.caso.comercial} comercial` : ""}</span>
          </div>
        </div>
        <section className="tarjeta" style={{ marginTop: 14, padding: "12px 14px" }}>
          <CabeceraSeccion titulo="Personas del cliente" n={cliente.length} derecha={<button className="btn btn-fantasma" style={{ padding: 0, fontSize: 12.5 }} onClick={() => abrir({ tipo: "persona", casoId: caso.id, cuentaId: c.id })}>+ Agregar</button>} />
          {cliente.length === 0 && <p style={{ fontSize: 12.5, color: "var(--faint)" }}>Nadie todavía. El dueño va primero.</p>}
          {cliente.map((p) => (
            <div key={p.id} style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "4px 0", fontSize: 13 }}>
              <span style={{ flex: 1 }}>{p.nombre}<span style={{ color: "var(--muted)", fontSize: 12 }}> · {p.rol}</span></span>
              {p.es_economic_buyer && <span className="tag11" style={{ color: p.gobernanza_confirmada ? "var(--verified)" : "var(--open-text)" }}>firma</span>}
              {p.puede_vetar && <span className="tag11" style={{ color: p.gobernanza_confirmada ? "var(--verified)" : "var(--open-text)" }}>veto</span>}
              {p.es_frontline && <span className="tag11" style={{ color: "var(--faint)" }}>frontline</span>}
              {(p.es_economic_buyer || p.puede_vetar) && !p.gobernanza_confirmada && <BotonGobernanza ctx={ctx} persona={p} />}
            </div>
          ))}
        </section>
      </nav>

      {/* ── La sesión elegida ── */}
      <section className="tarjeta">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 4 }}>
          <h2 className="subtitulo">{def.num} · {def.nombre}</h2>
          <span style={{ fontSize: 12, color: "var(--faint)", whiteSpace: "nowrap" }}>{def.dur}</span>
        </div>
        <p style={{ fontSize: 12.5, color: "var(--muted)", marginBottom: 12 }}>{def.quien} · {def.meta}</p>
        {mias.length === 0 ? (
          <Abierto style={{ marginBottom: 12 }}>
            Sin registrar. Se anota después de tenerla: fecha, asistentes, si hubo Plaud con consentimiento y, si la tienes, la transcripción.
            <div style={{ marginTop: 10 }}><button className="btn btn-primario" onClick={() => abrir({ tipo: "sesion", casoId: caso.id, cuentaId: c.id, personas: (personas ?? []).map((p) => ({ id: p.id, nombre: p.nombre })) })}>Registrar {def.nombre} →</button></div>
          </Abierto>
        ) : mias.map((s) => (
          <div key={s.id} style={{ padding: "10px 0", borderTop: "1px solid var(--border)" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
              <span className="mono">{fmtFecha(s.fecha, true)}{s.fecha.length > 10 ? ` ${s.fecha.slice(11, 16)}` : ""}</span>
              <span style={{ fontSize: 12.5, color: ESTADO_SESION[s.estado][1] }}>{ESTADO_SESION[s.estado][0]}</span>
              {s.grabada ? <Pastilla estado="muted">grabada</Pastilla> : <Pastilla estado="proposed">sin grabación</Pastilla>}
            </div>
            <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>{s.asistentes.map((a) => nombre.get(a) ?? a).join(", ")}</p>
            {s.transcripcion && (
              <details style={{ marginTop: 6 }}>
                <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--sky)" }}>
                  Transcripción{s.transcripcion_archivo ? ` · ${s.transcripcion_archivo}` : ""}
                </summary>
                <div style={{ marginTop: 6, maxHeight: 360, overflow: "auto", padding: "10px 12px", background: "var(--sunken)", borderRadius: 8, fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                  {s.transcripcion}
                </div>
              </details>
            )}
          </div>
        ))}
        {mias.length > 0 && candidatos && candidatos.some((h) => mias.some((s) => s.id === h.sesion_id)) && (
          <Link to={`/verificacion/${caso.id}`} className="btn btn-primario" style={{ textDecoration: "none", marginTop: 8, marginBottom: 4 }}>
            Verificar {candidatos.filter((h) => mias.some((s) => s.id === h.sesion_id)).length} hallazgos de esta sesión →
          </Link>
        )}
        {mias.length > 0 && (
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <button className="btn btn-primario" onClick={() => abrir({ tipo: "hallazgo", casoId: caso.id, cuentaId: c.id, personas: personas ?? [], sesiones: sesiones ?? [], sesionId: mias[mias.length - 1].id })}>+ Proponer hallazgo</button>
            <button className="btn btn-secundario" onClick={() => abrir({ tipo: "sesion", casoId: caso.id, cuentaId: c.id, personas: (personas ?? []).map((p) => ({ id: p.id, nombre: p.nombre })) })}>+ Otra sesión</button>
          </div>
        )}
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span className="tag11" style={{ color: "var(--faint)" }}>Lo que se escuchó</span>
            {def.id === "S1_platica" && <BotonArtefacto ctx={ctx} />}
          </div>
          {vista.hallazgos_muestra.length === 0 && <p style={{ fontSize: 12.5, color: "var(--faint)" }}>Citas y ansiedades verificadas aparecen aquí.</p>}
          {vista.hallazgos_muestra.slice(0, 5).map((h, i) => (
            <p key={i} style={{ fontSize: 13, lineHeight: 1.5, padding: "4px 0", color: "var(--text)" }}>
              <span className="tag11" style={{ color: h.tipo === "ansiedad" ? "var(--conflict)" : "var(--faint)", marginRight: 6 }}>{h.tipo === "quote" ? "cita" : h.tipo}</span>{h.contenido}
            </p>
          ))}
        </div>
      </section>

      {/* ── Los tres embudos y el cierre ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Embudos
          ctx={ctx}
          transiciones={transiciones ?? []}
          onSesion={() => abrir({ tipo: "sesion", casoId: caso.id, cuentaId: c.id, personas: (personas ?? []).map((p) => ({ id: p.id, nombre: p.nombre })) })}
          onPersona={() => abrir({ tipo: "persona", casoId: caso.id, cuentaId: c.id })}
          onHallazgo={() => abrir({ tipo: "hallazgo", casoId: caso.id, cuentaId: c.id, personas: personas ?? [], sesiones: sesiones ?? [] })}
        />
        {vista.invariantes.length > 0 && (
          <section className="tarjeta" style={{ borderColor: "var(--risk)" }}>
            <CabeceraSeccion titulo="Invariantes rotos" n={vista.invariantes.length} />
            {vista.invariantes.map((v, i) => <p key={i} style={{ fontSize: 12.5, color: "var(--risk)", padding: "3px 0" }}>{v.invariante} · {v.descripcion}</p>)}
          </section>
        )}
      </div>
    </div>
  );
}
